import React, { useEffect, useState } from "react";
import { X, Send, Loader2, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { PremiumInput } from "../brand/PremiumInput";
import { PremiumTextarea } from "../brand/PremiumTextarea";
import { FormFieldsSkeleton } from "../ui/operational-skeleton";

/**
 * RequestUpdateSheet — calm operational composer for the Blocker Strip.
 *
 * Fetches a prefilled draft from `/blockers/{id}/request-update/preview`
 * on open, lets the agent quick-edit the recipient + subject + body,
 * then posts to `/blockers/{id}/request-update` to send + log the
 * activity row. UX intentionally low-friction — one click to send,
 * no fancy formatting, no AI suggestions — this is a transaction
 * momentum tool, not a marketing composer.
 */
const _roleLabel = (t) => String(t || "participant").replace(/_/g, " ");

/* Shown only when the draft has no resolved owner: lists the deal's participants, none preselected. */
const RecipientChoices = ({ participants, value, onPick }) => (
  <div className="mt-1.5 space-y-1" role="radiogroup" aria-label="Choose recipient" data-testid="request-update-recipient-choices">
    <p className="text-[11px] text-amber-700" data-testid="request-update-recipient-unclear">
      Responsibility is unclear — choose who to contact.
    </p>
    {participants.map((p) => (
      <label key={p.id || p.email} className="flex items-center gap-2 text-[12px] text-slate-700 cursor-pointer">
        <input
          type="radio"
          name="request-update-recipient-choice"
          checked={value === p.email}
          onChange={() => onPick(p.email)}
          data-testid={`request-update-recipient-option-${p.participant_type || "participant"}`}
        />
        <span className="font-semibold">{p.full_name || p.email}</span>
        <span className="text-slate-500 capitalize">· {_roleLabel(p.participant_type)}</span>
      </label>
    ))}
  </div>
);

export const RequestUpdateSheet = ({ dealId, blocker, onClose, onSent, initialRecipient = null, participants = [] }) => {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sending, setSending] = useState(false);
  const [recipient, setRecipient] = useState("");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    setError(null);
    // iter-alignment · P20 — When the caller supplies an
    // `initialRecipient` (e.g. an enquiry's owner-role participant),
    // we ask the backend to override the spec-resolved email.  The
    // backend keeps the resolved name and swaps only the address, so
    // the greeting still reads naturally.
    const previewBody = initialRecipient?.email
      ? { recipient_email: initialRecipient.email }
      : {};
    api.post(`/api/v2/deals/${dealId}/blockers/${blocker.id}/request-update/preview`, previewBody)
      .then(({ data: p }) => {
        if (!mounted) return;
        setPreview(p);
        setRecipient(p?.recipient?.email || initialRecipient?.email || "");
        setSubject(p?.subject || "");
        setBody(p?.body || "");
      })
      .catch((e) => {
        if (!mounted) return;
        setError(e?.message || "Could not load draft.");
      })
      .finally(() => mounted && setLoading(false));
    return () => { mounted = false; };
  }, [dealId, blocker.id, initialRecipient?.email]);

  // Esc-to-close + keep keyboard flow predictable.
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const canSend = !sending && !!recipient && !!body.trim();

  const handleSend = async () => {
    if (!canSend) return;
    setSending(true);
    try {
      await api.post(`/api/v2/deals/${dealId}/blockers/${blocker.id}/request-update`, {
        recipient_email: recipient,
        subject,
        body,
      });
      onSent?.();
    } catch (e) {
      toast.error(e?.message || "Could not send request");
      setSending(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[70] flex items-start justify-center px-4 pt-[10vh]"
      onClick={onClose}
      data-testid="request-update-sheet"
    >
      <div className="absolute inset-0 bg-slate-950/40 backdrop-blur-sm" />
      <div
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="Request operational update"
        className="relative w-full max-w-[560px] rounded-2xl bg-white border border-slate-200 cf-shadow-xl overflow-hidden cf-fade-up"
      >
        <div className="px-5 py-4 border-b border-slate-100 flex items-start gap-3">
          <div className="min-w-0 flex-1">
            <p className="text-[10.5px] font-bold uppercase tracking-[0.16em] text-slate-500">Request update</p>
            <h2 className="text-[14.5px] font-bold text-slate-900 mt-0.5">{blocker.title}</h2>
            <p className="text-[11.5px] text-slate-500 mt-0.5">
              ChainFlow has prefilled a calm operational message. Edit or send as-is.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="w-8 h-8 rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-100 inline-flex items-center justify-center"
            data-testid="request-update-close"
          >
            <X size={14} />
          </button>
        </div>

        <div className="px-5 py-4 space-y-3">
          {loading ? (
            <FormFieldsSkeleton fields={3} testid="request-update-loading" />
          ) : error ? (
            <div className="rounded-lg bg-rose-50 border border-rose-200 px-3 py-2 text-rose-700 text-[12px] flex items-start gap-2">
              <AlertCircle size={13} className="mt-0.5 shrink-0" /> {error}
            </div>
          ) : (
            <div className="cf-hydrate-fade space-y-3">
              <div>
                <label className="text-[10.5px] font-bold uppercase tracking-[0.12em] text-slate-500">Recipient</label>
                {!preview?.recipient && !initialRecipient?.email && participants.some((p) => p?.email) && (
                  <RecipientChoices participants={participants.filter((p) => p?.email)} value={recipient} onPick={setRecipient} />
                )}
                <div className="mt-1">
                  <PremiumInput
                    type="email"
                    value={recipient}
                    onChange={setRecipient}
                    placeholder="recipient@example.com"
                    testid="request-update-recipient"
                  />
                </div>
                {(initialRecipient?.full_name || preview?.recipient?.full_name) && (
                  <p className="text-[11px] text-slate-500 mt-1">
                    Resolved: <span className="font-semibold text-slate-700">{initialRecipient?.full_name || preview?.recipient?.full_name}</span>
                    {(initialRecipient?.organisation_name || preview?.recipient?.organisation_name) ? ` · ${initialRecipient?.organisation_name || preview?.recipient?.organisation_name}` : ""}
                  </p>
                )}
              </div>

              <div>
                <label className="text-[10.5px] font-bold uppercase tracking-[0.12em] text-slate-500">Subject</label>
                <div className="mt-1">
                  <PremiumInput
                    value={subject}
                    onChange={setSubject}
                    testid="request-update-subject"
                  />
                </div>
              </div>

              <div>
                <label className="text-[10.5px] font-bold uppercase tracking-[0.12em] text-slate-500">Message</label>
                <div className="mt-1">
                  <PremiumTextarea
                    value={body}
                    onChange={setBody}
                    rows={6}
                    autoResize
                    minRows={4}
                    maxRows={10}
                    testid="request-update-body"
                  />
                </div>
                <p className="text-[10.5px] text-slate-500 mt-1.5">
                  Tone: professional, calm, operational. A timeline entry is created when sent.
                </p>
              </div>
            </div>
          )}
        </div>

        <div className="px-5 py-3 border-t border-slate-100 bg-slate-50/60 flex items-center justify-between">
          <p className="text-[10.5px] text-slate-500">
            <span className="font-semibold text-slate-700">ChainFlow</span> · operational momentum
          </p>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onClose}
              className="h-9 px-3 rounded-lg text-[12px] font-semibold text-slate-600 hover:text-slate-900 hover:bg-slate-100"
              data-testid="request-update-cancel"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSend}
              disabled={!canSend}
              className={
                "h-9 px-3.5 rounded-lg text-white text-[12px] font-bold inline-flex items-center gap-1.5 transition-all " +
                (canSend ? "bg-slate-900 hover:bg-slate-700" : "bg-slate-300 cursor-not-allowed")
              }
              data-testid="request-update-send"
            >
              {sending ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />}
              {sending ? "Sending…" : "Send"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RequestUpdateSheet;
